/**
 * Test hooks and perf HUD (P0-T05, SPEC-13 §2). Non-production builds expose
 * window.__KN with stats(), noAdapt and perf-runner entry points.
 */
import type { AbstractEngine, Scene } from 'babylonjs';

export interface KNStats {
  fps: number;
  frameMsP50: number;
  frameMsP95: number;
  frameMsMax: number;
  drawCalls: number;
  activeMeshes: number;
  triangles: number;
  jsMs: number;
  tier: string;
  gpu: string;
  renderScale: number;
  shed: string[];
  crowd: number;
  shedSteps: string[];
  /** ms between local render time and the server patch timestamp (P0-G03); 0 when offline */
  serverPatchLagMs: number;
  /** p95 of the patch lag over the rolling 60 s window (P0-G03) */
  serverPatchLagP95Ms: number;
}

// Babylon 9: no direct draw-call counter in SceneInstrumentation; use
// EngineInstrumentation where available and scene.activeMeshes / totalVertices
// for mesh+triangle counts. `drawCalls` reports the engine's active-mesh draw
// estimate and is calibrated against the on-device capture (SPEC-13 §2.1).
interface BabylonNs {
  SceneInstrumentation: new (
    scene: Scene,
    captureFrameTime?: boolean,
    captureRenderTime?: boolean,
  ) => {
    frameTimeCounter: { current: number };
    dispose(): void;
  };
}

export class FrameStats {
  private raf: number[] = [];
  private work: number[] = [];
  private last = 0;

  push(nowMs: number, workMs: number): void {
    if (this.last) this.raf.push(nowMs - this.last);
    this.last = nowMs;
    this.work.push(workMs);
    if (this.raf.length > 240) this.raf.shift();
    if (this.work.length > 240) this.work.shift();
  }

  get rafP95(): number {
    return p95(this.raf);
  }
  get workP95(): number {
    return p95(this.work);
  }
  /** median of the shortest 10% of rAF deltas over the window ≈ display refresh */
  get refreshMs(): number {
    if (this.raf.length < 10) return 16.7;
    const sorted = [...this.raf].sort((a, b) => a - b);
    const k = Math.max(1, Math.floor(sorted.length * 0.1));
    const slice = sorted.slice(0, k);
    slice.sort((a, b) => a - b);
    return slice[Math.floor(slice.length / 2)] ?? 16.7;
  }
  get p50(): number {
    const s = [...this.work].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)] ?? 0;
  }
  get max(): number {
    return this.work.length ? Math.max(...this.work) : 0;
  }
  get fps(): number {
    const r = p95(this.raf);
    return r > 0 ? Math.round(1000 / median(this.raf)) : 0;
  }
}

export function p95(samples: number[]): number {
  if (!samples.length) return 0;
  const s = [...samples].sort((a, b) => a - b);
  return s[Math.floor(s.length * 0.95)] ?? s[s.length - 1] ?? 0;
}

function median(samples: number[]): number {
  if (!samples.length) return 0;
  const s = [...samples].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] ?? 0;
}

/** window.__KN (SPEC-13 §2); installed only in non-production builds. */
export interface KNGlobal {
  stats(): KNStats;
  noAdapt: boolean;
  tier: string;
  gpu: string;
  /** force the next adaptive-quality shed step (gate P0-G08 evidence) */
  forceShed(): void;
  /** perf runner control (SPEC-13 §2.1) */
  perf?: { done: boolean; json(): unknown };
  /** set by forceShed(); the quality controller consumes and clears it */
  __forceShed?: boolean;
  /** wired by main.ts after the net client connects (P0-G03 metrics) */
  setNetSource?(src: { serverPatchLagMs: number; patchLagP95(): number }): void;
  scene?: Scene;
  engine?: AbstractEngine;
}

export function installKN(
  babylon: typeof import('babylonjs'),
  engine: AbstractEngine,
  scene: Scene,
  base: { tier: string; gpu: string; shed: string[] },
): KNGlobal {
  const ns = babylon as unknown as BabylonNs;
  const inst = new ns.SceneInstrumentation(scene, true, true);
  void inst;
  const fetch = (): void => {
    /* counters are live getters in Babylon 9 */
  };
  const kn: KNGlobal = {
    noAdapt: false,
    tier: base.tier,
    gpu: base.gpu,
    stats(): KNStats {
      fetch();
      return lastStats;
    },
    forceShed(): void {
      kn.__forceShed = true;
    },
    setNetSource(src): void {
      netSource = src;
    },
    scene,
    engine,
  };

  let netSource: { serverPatchLagMs: number; patchLagP95(): number } | null = null;
  let lastStats: KNStats = emptyStats(base);
  let lastFrameWorkMs = 0;

  Object.defineProperty(kn, '__lastStats', {
    get: () => lastStats,
    configurable: true,
  });

  scene.onAfterRenderObservable.add(() => {
    lastFrameWorkMs = engine.getDeltaTime();
  });

  scene.onBeforeRenderObservable.add(() => {
    fetch();
    lastStats = {
      ...lastStats,
      drawCalls: scene.getActiveMeshes().length,
      activeMeshes: scene.getActiveMeshes().length,
      triangles: triCount(scene),
      jsMs: Math.round(lastFrameWorkMs * 10) / 10,
      serverPatchLagMs: netSource ? netSource.serverPatchLagMs : 0,
      serverPatchLagP95Ms: netSource ? netSource.patchLagP95() : 0,
    };
  });

  (window as unknown as { __KN: KNGlobal }).__KN = kn;
  return kn;
}

/** Babylon 9 scene.getTotalVertices() can read 0 before world matrices are
 * computed; sum the active meshes as a fallback. */
function triCount(scene: Scene): number {
  const direct = scene.getTotalVertices();
  if (direct > 0) return Math.round(direct / 3);
  let verts = 0;
  const active = scene.getActiveMeshes();
  for (let i = 0; i < active.length; i++) {
    const m = active.data[i];
    if (m) verts += m.getTotalVertices();
  }
  return Math.round(verts / 3);
}

function emptyStats(base: { tier: string; gpu: string; shed: string[] }): KNStats {
  return {
    fps: 0,
    frameMsP50: 0,
    frameMsP95: 0,
    frameMsMax: 0,
    drawCalls: 0,
    activeMeshes: 0,
    triangles: 0,
    jsMs: 0,
    tier: base.tier,
    gpu: base.gpu,
    renderScale: 1,
    serverPatchLagMs: 0,
    serverPatchLagP95Ms: 0,
    shed: base.shed,
    crowd: 0,
    shedSteps: base.shed,
  };
}
